/**
 * QuantityStepper - Shared +/- quantity control
 *
 * Replaces the hand-rolled steppers in deck rows, list rows and the list card details panel.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';

export type QuantityStepperSize = 'sm' | 'md';
export type QuantityStepperOrientation = 'horizontal' | 'vertical';

export interface QuantityStepperProps {
  value: number;
  onIncrease: () => void;
  onDecrease: () => void;
  /** False disables the increase button (e.g. format copy limit reached). */
  canIncrease?: boolean;
  /** False disables the decrease button. */
  canDecrease?: boolean;
  /** Disables both buttons, e.g. while a mutation for this row is in flight. */
  disabled?: boolean;
  orientation?: QuantityStepperOrientation;
  size?: QuantityStepperSize;
  /** Override when decreasing does something other than subtract one (e.g. removes the row). */
  decreaseLabel?: string;
  increaseLabel?: string;
  className?: string;
}

const SIZE_STYLES: Record<QuantityStepperSize, { button: string; icon: string; value: string }> = {
  sm: { button: 'h-5 w-5', icon: 'h-2.5 w-2.5', value: 'text-sm' },
  md: { button: 'h-8 w-8', icon: 'h-3 w-3', value: 'text-base' },
};

export default function QuantityStepper({
  value,
  onIncrease,
  onDecrease,
  canIncrease = true,
  canDecrease = true,
  disabled = false,
  orientation = 'horizontal',
  size = 'sm',
  decreaseLabel = 'Decrease quantity',
  increaseLabel = 'Increase quantity',
  className,
}: QuantityStepperProps): ReactElement {
  const styles = SIZE_STYLES[size];
  const isVertical = orientation === 'vertical';

  const buttonClass = clsx(
    styles.button,
    'flex items-center justify-center rounded text-zinc-400 transition-colors',
    'hover:bg-zinc-700 hover:text-zinc-200',
    'focus-visible:ring-aether focus-visible:ring-2 focus:outline-none',
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-400',
  );

  const increaseButton = (
    <button type="button" onClick={onIncrease} disabled={disabled || !canIncrease} className={buttonClass} aria-label={increaseLabel}>
      <FaPlus className={styles.icon} />
    </button>
  );

  const decreaseButton = (
    <button type="button" onClick={onDecrease} disabled={disabled || !canDecrease} className={buttonClass} aria-label={decreaseLabel}>
      <FaMinus className={styles.icon} />
    </button>
  );

  return (
    <div className={clsx('flex items-center', isVertical ? 'flex-col gap-0.5' : 'gap-1', className)}>
      {isVertical ? increaseButton : decreaseButton}
      <span className={clsx('min-w-6 text-center font-medium text-zinc-200 tabular-nums', styles.value)}>{value}</span>
      {isVertical ? decreaseButton : increaseButton}
    </div>
  );
}
